import { FlightLookupError } from "../types";

const MIN_PREFIX_LENGTH = 2;
const MAX_PREFIX_LENGTH = 3;
const MIN_DIGITS = 1;
const MAX_DIGITS = 4;

// Yesterday comes from dash-data's archive, filled by its 03:30 HKT nightly ingest; today onwards
// is the live HKIA board.
const DAYS_BACK = 1;
const DAYS_FORWARD = 7;
const ARCHIVE_READY_HOUR_HKT = 5;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const HKT_OFFSET_MS = 8 * 60 * 60 * 1000;

const normalizeFlightNumber = (raw: string): string =>
  raw.replace(/\s+/g, "").toUpperCase();

/**
 * Two or three alphanumeric characters, then one to four digits, with an
 * optional trailing letter (R-A2 EC1). Deliberately not a single regex:
 * a naive `^[A-Z0-9]{2,3}\d{1,4}[A-Z]?$` is ambiguous on inputs such as
 * "CX54999" (2-letter code + 5 digits) because its greedy alphanumeric
 * prefix can "steal" a digit, matching as "CX5" + "4999" and letting an
 * over-long number pass. Taking the maximal trailing run of digits
 * removes that ambiguity while still allowing alphanumeric prefixes
 * (e.g. "5J123").
 */
const isValidFlightNumberFormat = (normalized: string): boolean => {
  let body = normalized;

  if (/^[A-Z]$/.test(body.slice(-1))) {
    body = body.slice(0, -1);
  }

  const digitsMatch = body.match(/\d+$/);
  const digits = digitsMatch ? digitsMatch[0] : "";
  const prefix = digits ? body.slice(0, body.length - digits.length) : body;

  return (
    digits.length >= MIN_DIGITS &&
    digits.length <= MAX_DIGITS &&
    prefix.length >= MIN_PREFIX_LENGTH &&
    prefix.length <= MAX_PREFIX_LENGTH
  );
};

const isWithinDateWindow = (isoDate: string, todayIso: string): boolean => {
  const target = new Date(`${isoDate}T00:00:00Z`).getTime();
  const today = new Date(`${todayIso}T00:00:00Z`).getTime();
  const diffDays = Math.round((target - today) / MS_PER_DAY);
  return diffDays >= -DAYS_BACK && diffDays <= DAYS_FORWARD;
};

/** Today's Hong Kong calendar date as YYYY-MM-DD, whatever the browser's own timezone. */
export const todayHktIso = (now: number = Date.now()): string =>
  new Date(now + HKT_OFFSET_MS).toISOString().slice(0, 10);

/** First date the lookup accepts (yesterday, HKT), as YYYY-MM-DD. */
export const minLookupDateIso = (todayIso: string = todayHktIso()): string =>
  new Date(new Date(`${todayIso}T00:00:00Z`).getTime() - DAYS_BACK * MS_PER_DAY)
    .toISOString()
    .slice(0, 10);

/**
 * True when `isoDate` is yesterday and it's too early in the Hong Kong morning for the nightly
 * archive to have landed — a "not found" then means "not archived yet", not "no such flight".
 */
export const isArchivePending = (isoDate: string, now: number = Date.now()): boolean =>
  isoDate === minLookupDateIso(todayHktIso(now)) &&
  new Date(now + HKT_OFFSET_MS).getUTCHours() < ARCHIVE_READY_HOUR_HKT;

/** Last date the lookup accepts, as YYYY-MM-DD. */
export const maxLookupDateIso = (todayIso: string = todayHktIso()): string =>
  new Date(new Date(`${todayIso}T00:00:00Z`).getTime() + DAYS_FORWARD * MS_PER_DAY)
    .toISOString()
    .slice(0, 10);

export interface ValidationResult {
  valid: boolean;
  error?: FlightLookupError["type"];
}

export const validateFlightQuery = (
  flightNumberRaw: string,
  isoDate: string,
  todayIso: string = todayHktIso(),
): ValidationResult => {
  const normalized = normalizeFlightNumber(flightNumberRaw);
  if (!isValidFlightNumberFormat(normalized)) {
    return { valid: false, error: "malformed" };
  }
  if (!isWithinDateWindow(isoDate, todayIso)) {
    return { valid: false, error: "outOfRange" };
  }
  return { valid: true };
};
